import type { Tokens } from '../augmentation'
import { spacing } from '../spacing'
import { getIconButtonPalette } from './helpers/get-icon-button-palette-color'
import { type ComponentCreation, type ComponentStyleProps } from './types'

const sizes = (factor: number, squareWidthFactor?: number) => ({
  height: spacing(factor),
  width: spacing(factor),

  '&.DhlIconButton--square': {
    width: spacing(squareWidthFactor || factor + 1),
  },
})

const iconAndProgressSizes = (progressFactor = 3) => ({
  '& .MuiSvgIcon-root': {
    fontSize: spacing(3),
  },
  '& .MuiCircularProgress-root': {
    height: `${spacing(progressFactor)} !important`,
    width: `${spacing(progressFactor)} !important`,
  },
})

type StyleProps = ComponentStyleProps<'MuiIconButton'>

export const createMuiIconButton = ({ palette, misc }: Pick<Tokens, 'palette' | 'misc'>) =>
  ({
    MuiIconButton: {
      defaultProps: {
        variant: 'ghost',
        size: 'medium',
        color: 'dark',
      },
      styleOverrides: {
        root: {
          '&.DhlIconButton--square': {
            borderRadius: misc.borderRadius(),
          },

          ...misc.createFocusVisibleState({
            boxShadow: 'none',
            outline: misc.borderStyle('info.main', 3),
            outlineOffset: misc.borderSize(2),
          }),

          variants: [
            {
              props: { color: 'inherit', variant: 'contained' },
              style: {
                backgroundColor: palette.grey['300'],
                ...misc.createHoverOrActiveState({
                  backgroundColor: palette.grey['400'],
                }),
              },
            },
            {
              props: { color: 'inherit', variant: 'outlined' },
              style: {
                backgroundColor: 'transparent',
                borderWidth: misc.borderSize(2),
                borderStyle: 'solid',
                ...misc.createHoverOrActiveState({
                  backgroundColor: palette.grey['200'],
                }),
              },
            },
            {
              props: { color: 'inherit', variant: 'ghost' },
              style: {
                backgroundColor: 'transparent',
                border: 0,
                ...misc.createHoverOrActiveState({
                  backgroundColor: palette.dark.states.hover,
                }),
              },
            },
            {
              props: { variant: 'contained' },
              style: ({ color, theme }: StyleProps) => {
                const currentPallet = getIconButtonPalette(theme?.palette ?? palette, color)
                return currentPallet
                  ? {
                      color: currentPallet.contrastText,
                      backgroundColor: currentPallet.main,
                      ...misc.createHoverOrActiveState({
                        backgroundColor:
                          !!color && ['dark', 'light'].includes(color)
                            ? currentPallet.states.hover
                            : currentPallet.dark,
                        ...(color === 'dark' ? { color: currentPallet.dark } : {}),
                      }),

                      ':disabled': {
                        backgroundColor: palette.action.disabledBackground,
                        borderWidth: misc.borderSize(2),
                      },

                      ':not(:focus-visible):not(:disabled):active': {
                        opacity: 0.8, // set an opacity on the entire Button!
                      },
                    }
                  : {}
              },
            },
            /**
             * Outlined IconButton, Mui sets an opacity on the border:
             */
            {
              props: { variant: 'outlined' },
              style: ({ color, theme }: StyleProps) => {
                const currentPallet = getIconButtonPalette(theme?.palette ?? palette, color)
                return currentPallet
                  ? {
                      borderWidth: misc.borderSize(2),
                      borderStyle: 'solid',
                      color: color === 'alternative' ? currentPallet.contrastText : currentPallet.notContained,
                      ':disabled': {
                        backgroundColor: palette.action.disabledBackground,
                        borderWidth: misc.borderSize(2),
                      },

                      ...misc.createHoverOrActiveState({
                        backgroundColor: currentPallet.states.hover,
                      }),

                      ':not(:focus-visible):not(:disabled)': {
                        borderColor: currentPallet.main,
                        '&:active': {
                          opacity: 0.8,
                        },
                      },
                    }
                  : {}
              },
            },
            /**
             * Ghost IconButton:
             */
            {
              props: { variant: 'ghost' },
              style: ({ color, theme }: StyleProps) => {
                const currentPallet = getIconButtonPalette(theme?.palette ?? palette, color)
                return currentPallet
                  ? {
                      ...(color === 'alternative'
                        ? { color: currentPallet.contrastText }
                        : { color: currentPallet.notContained }),
                      border: 0,
                      ':disabled': {
                        backgroundColor: palette.action.disabledBackground,
                        borderWidth: misc.borderSize(2),
                      },

                      ...misc.createHoverOrActiveState({
                        backgroundColor: currentPallet.states.hover,
                      }),

                      ':not(:focus-visible):not(:disabled):active': {
                        opacity: 0.8,
                      },
                    }
                  : {}
              },
            },
          ],
        },
        sizeXs: {
          ...sizes(3.5, 5),
          ...iconAndProgressSizes(2),
          '& .MuiSvgIcon-root': {
            fontSize: spacing(3),
          },
        },
        sizeSmall: {
          ...sizes(5),
          '& .MuiSvgIcon-root': {
            fontSize: spacing(3),
          },
        },
        sizeMedium: {
          ...sizes(6),
          ...iconAndProgressSizes(),
        },
        sizeLarge: {
          ...sizes(7),
          ...iconAndProgressSizes(),
        },
      },
    },
  }) satisfies ComponentCreation
